import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import dayjs from 'dayjs';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  CallIntelAccordion,
  generateTrackingId,
  getHighlightedCitation,
  TaskAccordion,
  UnsavedChangesModal,
} from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { CallQualityAccordionItem } from '@frontend/phone-panels';
import { theme } from '@frontend/theme';
import { Accordion, useModalControl } from '@frontend/design-system';
import { useCallInsightController } from '../../hooks';
import { useCallInsightPanelData } from '../../hooks/use-call-insight-panel-data';
import { useCallInsightPanelState } from '../../hooks/use-call-insight-panel-state';
import { useCallInsightPanelShallowStore } from '../../hooks/use-call-insight-panel-store';
import { useTaskHandlers } from '../../hooks/use-task-handlers';
import { CallInsightAccordionType, ScreenBaseProps, ScreenType } from '../../types';
import { CallInsightContent } from '../call-insight-content';
import { CallInsightHeader } from '../call-insight-header';
import { NavigationButtons } from '../navigation-buttons';
import { CallDetailsSection } from '../sections/call-details-section';
import { CallInsightContactSection } from '../sections/call-insight-contact-section';
import { CallInsightTagsSection } from '../sections/call-insight-tags-section';
import { CallRecordingSection } from '../sections/call-recording-section';

export type CallInsightScreenProps = ScreenBaseProps<ScreenType.CALL_INSIGHTS>;

export const CallInsightScreen = ({ panelStyles, source, onClose, setScreen }: CallInsightScreenProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { modalProps, triggerProps } = useModalControl();

  const { isLoading, emitEvent, goToNextCall, goToPreviousCall } = useCallInsightController({
    source,
  });
  const { contexts, closeContext, updateCallInsight } = useCallInsightPanelShallowStore(
    'contexts',
    'closeContext',
    'updateCallInsight'
  );

  // Extract context and call info
  const panelContext = contexts[source];
  const callRecord = panelContext?.selectedCall as HydratedCallRecord;
  const hasNextCall = !!panelContext?.nextCallItem;
  const hasPreviousCall = !!panelContext?.previousCallItem;
  const showCallIntelInsights = !!callRecord.callIntelInsights;
  const {
    defaultAccordion,
    isDemoAccount,
    highlightedTaskId: defaultHighlightedTaskId,
    defaultCallIntelTab,
  } = panelContext;

  const {
    callMetadata,
    callRecordingPath,
    contactInfo,
    hasCallIntelAccess,
    hasAccesstoCallRecording,
    isLoadingCallMetadata,
    isTaskFetching,
    isTasksEnabled,
    networkPerformance,
    taskQueryKey,
    refetchCallMetadata,
  } = useCallInsightPanelData({ callRecord, isDemoAccount, source });

  const {
    activeAccordion,
    highlightedTaskId,
    unsavedChangesConfig,
    setActiveAccordion,
    setHighlightedTaskId,
    setUnsavedChangesConfig,
  } = useCallInsightPanelState({
    defaultAccordion,
    defaultHighlightedTaskId,
    showCallIntelInsights: showCallIntelInsights && hasCallIntelAccess,
    channelId: callRecord.channelId, // Re-init when channelId changes
  });

  const { handleTasksUpdate, handleTaskDelete } = useTaskHandlers({
    queryKey: taskQueryKey,
    emitEvent,
    updateCallInsight,
  });

  const highlightedCitation = useMemo(
    () => getHighlightedCitation(callMetadata.tasks, highlightedTaskId),
    [callMetadata.tasks, highlightedTaskId]
  );

  const confirmBeforeAction = useCallback(
    (action: () => void) => {
      const { dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
      if (!dontAskAgain && hasUnsavedChanges) {
        setUnsavedChangesConfig({ actionType: 'SHOW_MODAL', hasUnsavedChanges: true });
      } else {
        action();
      }
    },
    [setUnsavedChangesConfig, unsavedChangesConfig]
  );

  const handleAccordionChange = useCallback(
    (value: string | null) => {
      setActiveAccordion(value as CallInsightAccordionType | null);
      setHighlightedTaskId(null);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleUpdateCall = useCallback(
    (call: CallIntelTypes.Call) => {
      updateCallInsight({ updatedCallIntelInsights: call as Call });
      emitEvent('callAnalysisUpdated', { call: call as Call });
    },
    [emitEvent, updateCallInsight]
  );

  const handleHighlightTask = useCallback(
    (taskId: string) => {
      setActiveAccordion('callIntel');
      setHighlightedTaskId(taskId);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleTagChange = useCallback(
    (
      action: 'added' | 'removed',
      { callRecord, tagId, updatedTagIds }: { callRecord: HydratedCallRecord; tagId: string; updatedTagIds: string[] }
    ) => {
      emitEvent(action === 'added' ? 'callTagAdded' : 'callTagRemoved', { callRecord, tagId });
      emitEvent('callTagsUpdated', { callRecord, tagIds: updatedTagIds });
    },
    [emitEvent]
  );

  const handleClose = useCallback(() => {
    const { hasUnsavedChanges, dontAskAgain } = unsavedChangesConfig;
    if (!dontAskAgain && hasUnsavedChanges) {
      setUnsavedChangesConfig({ hasUnsavedChanges: true, actionType: 'SHOW_MODAL' });
      return;
    }
    closeContext();
    !!onClose && onClose();
  }, [closeContext, onClose, setUnsavedChangesConfig, unsavedChangesConfig]);

  useEffect(() => {
    const { actionType, dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
    if (!dontAskAgain && actionType === 'SHOW_MODAL' && hasUnsavedChanges) {
      triggerProps.onClick();
    }
  }, [triggerProps, unsavedChangesConfig]);

  return (
    <div css={[styles.wrapper, panelStyles]} data-testid='call-insight-screen'>
      <CallInsightHeader onClose={handleClose} />
      <CallInsightContent isLoading={isLoading}>
        <CallInsightContent.Lead>
          <CallInsightContactSection
            callDirection={callRecord?.direction ?? ''}
            contact={{
              ...contactInfo,
              defaultName: t('Unknown'),
            }}
            locationId={contactInfo.locationId}
          />
          {hasAccesstoCallRecording && <CallRecordingSection call={callRecord} callRecordingPath={callRecordingPath} />}
          <CallInsightTagsSection
            call={callRecord}
            source={source}
            onTagChange={handleTagChange}
            isDemoAccount={isDemoAccount}
          />
          <Divider css={styles.divider} />
        </CallInsightContent.Lead>
        <CallInsightContent.Body>
          <Accordion controlledValue={activeAccordion} css={styles.accordion} onChange={handleAccordionChange}>
            {showCallIntelInsights && hasCallIntelAccess && (
              <Accordion.Item
                value='callIntel'
                data-testid='call-intelligence-accordion'
                trackingId={generateTrackingId({
                  prefix: 'call-insight-panel',
                  component: 'call-intel-accordion',
                  isDemoAccount,
                })}
              >
                <CallIntelAccordion.Header />
                <CallIntelAccordion.Body
                  call={callRecord.callIntelInsights as CallIntelTypes.Call}
                  isLoading={isLoadingCallMetadata || isTaskFetching}
                  isDemoAccount={isDemoAccount}
                  refetchCallMetadata={refetchCallMetadata}
                  callMetadata={callMetadata}
                  highlightedCitation={highlightedCitation}
                  onUpdateCall={handleUpdateCall}
                  unsavedEditChangesConfig={unsavedChangesConfig}
                  setUnsavedEditChangesConfig={setUnsavedChangesConfig}
                  defaultCallIntelTab={defaultCallIntelTab}
                />
              </Accordion.Item>
            )}

            {showCallIntelInsights && (
              <Accordion.Item
                value='tasks'
                data-testid='tasks-accordion'
                trackingId={generateTrackingId({
                  prefix: 'call-insight-panel',
                  component: 'task-accordion',
                  isDemoAccount,
                })}
              >
                <TaskAccordion.Header tasks={isTasksEnabled ? callMetadata.tasks : []} />
                <TaskAccordion.Body
                  call={callRecord.callIntelInsights as CallIntelTypes.Call}
                  callParticipantsMetadata={callMetadata.callParticipantsMetadata}
                  isLoading={isLoadingCallMetadata || isTaskFetching}
                  tasks={callMetadata.tasks}
                  isDemoAccount={isDemoAccount}
                  onHighlightTask={handleHighlightTask}
                  onUpdateTasks={handleTasksUpdate}
                  onDeleteTask={handleTaskDelete}
                  isTasksEnabled={isTasksEnabled}
                />
              </Accordion.Item>
            )}

            <Accordion.Item
              value='aboutThisCall'
              data-testid='call-details-accordion'
              trackingId={generateTrackingId({
                prefix: 'CallInsights',
                component: 'AboutThisCall',
                isDemoAccount,
              })}
            >
              <CallDetailsSection
                call={callRecord}
                onClick={() => {
                  const { firstName, lastName } = callRecord.person ?? {};
                  const hasDefinedName = firstName || lastName;
                  let callFrom;
                  if (hasDefinedName) callFrom = `${firstName} ${lastName}`;
                  else if (callRecord.callerName && !!isNaN(+callRecord.callerName)) {
                    callFrom = callRecord.callerName;
                  } else {
                    callFrom = formatPhoneNumber(callRecord.callerNumber);
                  }

                  setScreen({
                    screenType: ScreenType.CALL_PATH_MAPPING,
                    data: {
                      startTime: callRecord.startedAt || '',
                      channelId: callRecord.channelId || '',
                      callFrom: callFrom || '',
                      aiCallMetadata: callRecord.aiMetadata,
                      callStatus: callRecord.status || '',
                      callStartTimeFormatted: dayjs(callRecord.startedAt).format('MMM DD YYYY, hh:mm A'),
                    },
                  });
                }}
              />
            </Accordion.Item>

            {networkPerformance.showQualityAccordion && (
              <Accordion.Item
                value='callQuality'
                data-testid='call-quality-accordion'
                trackingId={generateTrackingId({
                  prefix: 'call-insight-panel',
                  component: 'call-quality-accordion',
                  isDemoAccount,
                })}
              >
                <CallQualityAccordionItem
                  call={callRecord}
                  hasIssues={networkPerformance.hasPerformanceIssue}
                  onClick={() => {
                    setScreen({ screenType: ScreenType.CALL_QUALITY });
                  }}
                />
              </Accordion.Item>
            )}
          </Accordion>
        </CallInsightContent.Body>
        <CallInsightContent.Footer>
          <NavigationButtons
            disableNext={!hasNextCall}
            disablePrevious={!hasPreviousCall}
            onNextClick={() => confirmBeforeAction(goToNextCall)}
            onPreviousClick={() => confirmBeforeAction(goToPreviousCall)}
            trackingIdBase='call-insight-panel'
          />
        </CallInsightContent.Footer>
      </CallInsightContent>
      {unsavedChangesConfig.hasUnsavedChanges && unsavedChangesConfig.actionType === 'SHOW_MODAL' && (
        <UnsavedChangesModal
          modalProps={modalProps}
          unsavedEditChangesConfig={unsavedChangesConfig}
          setUnsavedEditChangesConfig={setUnsavedChangesConfig}
        />
      )}
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
  `,
  accordion: css`
    flex: 1 1 100%;

    > div {
      box-shadow: none;
      border: 1px solid ${theme.colors.neutral20};
      margin-bottom: ${theme.spacing(2)};
    }

    > div h3 button section div svg {
      fill: ${theme.colors.neutral70};
    }

    div[data-active='true'] h3 button {
      background-color: ${theme.colors.neutral5};
      border-bottom: 1px solid ${theme.colors.neutral5};

      section div svg {
        fill: ${theme.colors.neutral90};
      }
    }
  `,
  feedbackFooter: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  `,
  divider: css`
    margin: ${theme.spacing(0)};
    border-color: ${theme.colors.neutral20};
  `,
};
